import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { PageHeader } from '../../../components/page-header';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/toast';
import { cn } from '../../../lib/utils';
import {
  ExitSurveyReason,
  SeatInfo,
  getSeatInfo,
  submitExitSurvey,
  applyDiscount,
  extendTrial,
  cancelSubscription,
} from '../../../../application/routes/settings/components/PaymentMethods/paymentApi';
import { DEV_MOCK, MOCK_SEAT_INFO } from './mock';
import { CANCEL_REASON_KEY_PREFIX } from '../../../components/WelcomeBackModal';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

// ─── Types ──────────────────────────────────────────────────────────────────

interface SurveyOption {
  id: ExitSurveyReason;
  label: string;
}

interface FollowUp {
  body: string;
  showFreeText: boolean;
  freeTextPlaceholder?: string;
  primaryCta: string;
  altCta?: string;
}

type UserSegment = 'trialing' | 'quarterly' | 'annual';

// ─── Constants ──────────────────────────────────────────────────────────────

const SURVEY_OPTIONS: SurveyOption[] = [
  { id: 'too_expensive', label: 'It was too expensive.' },
  {
    id: 'not_enough_time',
    label: "I didn't have enough time to try the feature.",
  },
  { id: 'too_many_issues', label: 'I ran into too many issues or bugs.' },
  {
    id: 'didnt_match_advertised',
    label: "The product didn't match what was advertised.",
  },
];

// ─── Follow-up copy per segment ─────────────────────────────────────────────

function getFollowUps(
  segment: UserSegment
): Record<ExitSurveyReason, FollowUp> {
  if (segment === 'trialing') {
    return {
      too_expensive: {
        body: "We hear you. We'd like to offer you 20% off — either plan, your choice. That brings Starter quarterly to $60/seat/mo or Starter annual to $40/seat/mo (plus 10 free estimates and 10 measurement reports every year). This offer is available right now before you go.",
        showFreeText: false,
        primaryCta: 'Get 20% off quarterly — $60/seat/mo',
      },
      not_enough_time: {
        body: "That makes sense — roofing is busy. We'd love to give you more time. We can add 7 days to your trial so you can explore FieldInspect when things slow down. No commitment, no charge.",
        showFreeText: false,
        primaryCta: 'Add 7 days to my trial',
      },
      too_many_issues: {
        body: "We're sorry about that. Our team would genuinely like to understand what you ran into — both to fix it for you and to make it right for others. We'd like to add 14 days to your trial and 3 extra free estimates so you can give it a fair shot once we've addressed your issues. We'll notify you as fixes are applied.",
        showFreeText: true,
        freeTextPlaceholder: 'What went wrong? (optional)',
        primaryCta: 'Send feedback + extend my trial',
        altCta: 'Talk to support',
      },
      didnt_match_advertised: {
        body: "That's important feedback, and we take it seriously. We want to understand exactly where the gap was — what were you expecting that you didn't find?",
        showFreeText: true,
        freeTextPlaceholder: 'What did you expect? (optional)',
        primaryCta: 'Send feedback',
        altCta: 'Talk to the team',
      },
    };
  }

  if (segment === 'annual') {
    return {
      too_expensive: {
        body: "We hear you. We'd like to offer you 20% off your next annual term — locking in $40/seat/mo when you renew. You keep all your current annual benefits including your free estimates and measurement reports at the current rate.",
        showFreeText: false,
        primaryCta: 'Apply 20% off my next renewal',
      },
      not_enough_time: {
        body: "We don't want you to leave without having actually experienced what FieldInspect can do. We'd like to assign you a dedicated onboarding specialist who will work with you personally to get you set up — at a time that works for your schedule, no rush.",
        showFreeText: false,
        primaryCta: 'Book a dedicated onboarding session',
      },
      too_many_issues: {
        body: "We're genuinely sorry. For an annual customer this is especially unacceptable and we want to make it right. We're adding you to our priority fix queue — you'll have a direct line to our support team and we'll send you updates as each issue is resolved. We're also adding 5 free estimates to your account so you can properly test things once the fixes are in.",
        showFreeText: true,
        freeTextPlaceholder: 'What went wrong? (optional but really helpful)',
        primaryCta: 'Send feedback + stay on my plan',
        altCta: 'Talk to support directly now',
      },
      didnt_match_advertised: {
        body: "That's serious feedback and we're not taking it lightly. A senior member of our team will reach out to you personally within a few hours — not a bot, not a support ticket. We want to understand exactly what you were expecting and where we fell short.",
        showFreeText: true,
        freeTextPlaceholder: 'What did you expect? (optional)',
        primaryCta: 'Send feedback',
        altCta: 'Talk to someone right now',
      },
    };
  }

  // Quarterly (paid, not trialing)
  return {
    too_expensive: {
      body: "We hear you. We'd like to offer you 20% off your next quarter right now — that brings you down to $60/seat/mo. Or if you switch to annual, you'd pay $50/seat/mo and get 10 free estimates and 10 measurement reports every year.",
      showFreeText: false,
      primaryCta: 'Apply 20% off — stay on quarterly at $60/seat/mo',
      altCta: 'Switch to annual — $50/seat/mo + free credits',
    },
    not_enough_time: {
      body: "Roofing doesn't slow down — we get it. Our team would love to get on a quick call and walk you through the parts of FieldInspect that save the most time. It's 20 minutes and we'll make it worth your while.",
      showFreeText: false,
      primaryCta: 'Book a call with our team',
    },
    too_many_issues: {
      body: "We're sorry — that's not the experience we want you to have. Tell us what you ran into and we'll loop in our team directly. We'll also add 3 free estimates to your account so you have room to test things properly once the issues are fixed. We'll notify you as each fix is applied.",
      showFreeText: true,
      freeTextPlaceholder: 'What went wrong? (optional but really helpful)',
      primaryCta: 'Send feedback + talk to our team',
    },
    didnt_match_advertised: {
      body: "That's important feedback and we take it seriously. We want to understand exactly where the gap was — what were you expecting that you didn't find? A member of our team will reach out personally within one business day.",
      showFreeText: true,
      freeTextPlaceholder: 'What did you expect? (optional)',
      primaryCta: 'Send feedback',
      altCta: 'Talk to the team now',
    },
  };
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ExitSurveyPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const orgId: string | undefined = (global as any)?.me?.orgID;

  const [seatInfo, setSeatInfo] = useState<SeatInfo | null>(null);
  const [selectedReason, setSelectedReason] = useState<ExitSurveyReason | null>(
    null
  );
  const [freeText, setFreeText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const fetchSeatInfo = useCallback(async () => {
    if (DEV_MOCK) {
      setSeatInfo(MOCK_SEAT_INFO as unknown as SeatInfo);
      return;
    }
    if (!orgId) {
      return;
    }
    try {
      const res = await getSeatInfo(orgId);
      if (res.success && res.data) {
        setSeatInfo(res.data);
      }
    } catch {
      // Non-critical
    }
  }, [orgId]);

  useEffect(() => {
    fetchSeatInfo();
  }, [fetchSeatInfo]);

  // Determine user segment
  const isAnnual =
    seatInfo?.billingTerm?.toLowerCase() === 'yearly' ||
    seatInfo?.billingTerm?.toLowerCase() === 'annual';
  const isTrialing = seatInfo?.subscriptionStatus === 'trialing';
  const segment: UserSegment = isTrialing
    ? 'trialing'
    : isAnnual
    ? 'annual'
    : 'quarterly';

  const followUps = getFollowUps(segment);
  const followUp = selectedReason ? followUps[selectedReason] : null;

  const storeReason = (reason: ExitSurveyReason) => {
    if (orgId) {
      localStorage.setItem(`${CANCEL_REASON_KEY_PREFIX}${orgId}`, reason);
    }
  };

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handlePrimaryCta = async () => {
    if (!selectedReason) {
      return;
    }

    if (DEV_MOCK) {
      setSubmitting(true);
      await new Promise((resolve) => setTimeout(resolve, 800));
      if (selectedReason === 'too_expensive') {
        toast.success('20% discount applied to your plan');
        setSubmitting(false);
        navigate('/v2/settings/billing');
        return;
      } else if (selectedReason === 'not_enough_time') {
        if (segment === 'trialing') {
          toast.success('7 days added to your trial');
        } else {
          toast.success('Call booking request sent');
        }
        setSubmitting(false);
        navigate('/v2/settings/billing');
        return;
      } else if (selectedReason === 'too_many_issues') {
        const credits = segment === 'annual' ? 5 : 3;
        if (segment === 'trialing') {
          toast.success(
            `Trial extended by 14 days with ${credits} bonus estimates`
          );
        } else {
          toast.success(
            `Feedback sent. ${credits} free estimates added to your account.`
          );
        }
      } else {
        toast.success('Thank you for your feedback');
      }
      setSubmitting(false);
      setSubmitted(true);
      return;
    }

    if (!orgId) {
      return;
    }

    try {
      setSubmitting(true);
      storeReason(selectedReason);
      await submitExitSurvey(orgId, selectedReason, freeText || undefined);

      if (selectedReason === 'too_expensive') {
        const res = await applyDiscount(orgId, 20);
        if (res.success) {
          toast.success('20% discount applied to your plan');
          navigate('/v2/settings/billing');
          return;
        } else {
          toast.error(res.error || 'Failed to apply discount');
        }
      } else if (selectedReason === 'not_enough_time') {
        if (segment === 'trialing') {
          const res = await extendTrial(orgId, 7);
          if (res.success) {
            toast.success('7 days added to your trial');
            navigate('/v2/settings/billing');
            return;
          } else {
            toast.error(res.error || 'Failed to extend trial');
          }
        } else {
          toast.success('Our team will reach out to schedule a call.');
          navigate('/v2/settings/billing');
          return;
        }
      } else if (selectedReason === 'too_many_issues') {
        if (segment === 'trialing') {
          const res = await extendTrial(orgId, 14, 3);
          if (res.success) {
            toast.success('Trial extended by 14 days with 3 bonus estimates');
          } else {
            toast.error(res.error || 'Failed to extend trial');
          }
        } else {
          const credits = segment === 'annual' ? 5 : 3;
          const res = await extendTrial(orgId, 0, credits);
          if (res.success) {
            toast.success(
              `Feedback sent. ${credits} free estimates added to your account.`
            );
          } else {
            toast.error(res.error || 'Failed to add credits');
          }
        }
      } else if (selectedReason === 'didnt_match_advertised') {
        toast.success('Thank you for your feedback. Our team will follow up.');
      }

      setSubmitted(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAltCta = async () => {
    if (!selectedReason) {
      return;
    }

    if (DEV_MOCK) {
      setSubmitting(true);
      await new Promise((resolve) => setTimeout(resolve, 800));
      if (selectedReason === 'too_expensive' && segment === 'quarterly') {
        toast.success('Switched to annual billing');
        setSubmitting(false);
        navigate('/v2/settings/billing');
        return;
      }
      toast.success('Our team will reach out shortly.');
      setSubmitting(false);
      setSubmitted(true);
      return;
    }

    if (!orgId) {
      return;
    }

    try {
      setSubmitting(true);
      await submitExitSurvey(orgId, selectedReason, freeText || undefined);
      toast.success('Our team will reach out shortly.');
      setSubmitted(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    if (DEV_MOCK) {
      setDismissing(true);
      await new Promise((resolve) => setTimeout(resolve, 800));
      toast.success('Your plan has been cancelled.');
      setDismissing(false);
      navigate('/v2/settings/billing/cancel/confirmed');
      return;
    }

    if (!orgId) {
      return;
    }

    try {
      setDismissing(true);
      if (selectedReason) {
        await submitExitSurvey(orgId, selectedReason, freeText || undefined);
      }
      const res = await cancelSubscription(orgId, false);
      if (res.success) {
        navigate('/v2/settings/billing/cancel/confirmed');
      } else {
        toast.error(res.error || 'Failed to cancel subscription');
      }
    } catch {
      toast.error('Unable to cancel. Please try again.');
    } finally {
      setDismissing(false);
    }
  };

  const handleSkip = async () => {
    if (DEV_MOCK) {
      navigate('/v2/settings/billing');
      return;
    }
    if (!orgId || !selectedReason) {
      navigate('/v2/settings/billing');
      return;
    }
    try {
      await submitExitSurvey(orgId, selectedReason, freeText || undefined);
    } catch {
      // Non-critical
    }
    navigate('/v2/settings/billing');
  };

  // ─── Render: Thank you screen ────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Thank You"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto text-center space-y-6 py-12">
            <h1 className="font-serif text-[28px] font-normal leading-tight tracking-[-0.02em] text-foreground">
              Thanks for your feedback
            </h1>
            <p className="text-muted-foreground">
              We appreciate you taking the time. Your input helps us improve
              FieldInspect for everyone.
            </p>
            <Button onClick={() => navigate('/v2/settings/billing')}>
              Back to Billing
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Survey ───────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Exit Survey"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Back link */}
          <button
            onClick={() => navigate('/v2/settings/billing')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
            Back to Billing
          </button>

          {/* Segment indicator (dev only) */}
          {DEV_MOCK && (
            <div className="px-3 py-1.5 rounded-md bg-amber-50 dark:bg-amber-900/10 border border-amber-300 dark:border-amber-700">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Segment: <strong>{segment}</strong> (change
                subscriptionStatus/billingTerm in mock.ts)
              </p>
            </div>
          )}

          {/* Question */}
          <div className="space-y-2">
            <h1 className="font-serif text-[28px] font-normal leading-tight tracking-[-0.02em] text-foreground">
              Why do you want to cancel?
            </h1>
            <p className="text-sm text-muted-foreground">
              Your feedback helps us build a better product. This takes 10
              seconds.
            </p>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {SURVEY_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => {
                  setSelectedReason(option.id);
                  setFreeText('');
                }}
                className={cn(
                  'w-full text-left p-4 rounded-xl border transition-colors',
                  selectedReason === option.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-card hover:border-primary/50'
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'size-5 rounded-full border-2 flex items-center justify-center shrink-0',
                      selectedReason === option.id
                        ? 'border-primary'
                        : 'border-muted-foreground/40'
                    )}
                  >
                    {selectedReason === option.id && (
                      <div className="size-2.5 rounded-full bg-primary" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {option.label}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Follow-up section */}
          {followUp && (
            <div className="border border-border rounded-xl p-6 bg-card space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {followUp.body}
              </p>

              {followUp.showFreeText && (
                <textarea
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  placeholder={followUp.freeTextPlaceholder}
                  className="w-full min-h-[100px] p-3 rounded-lg border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground resize-y focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              )}

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handlePrimaryCta}
                  disabled={submitting || dismissing}
                >
                  {submitting ? (
                    <Loader2 className="size-4 animate-spin mr-2" />
                  ) : null}
                  {followUp.primaryCta}
                </Button>
                {followUp.altCta && (
                  <Button
                    variant="outline"
                    onClick={handleAltCta}
                    disabled={submitting || dismissing}
                  >
                    {followUp.altCta}
                  </Button>
                )}
              </div>

              {/* Dismiss — cancel anyway */}
              <div className="pt-2 border-t border-border">
                <button
                  onClick={handleDismiss}
                  disabled={submitting || dismissing}
                  className="text-sm text-muted-foreground hover:text-destructive transition-colors"
                >
                  {dismissing ? 'Cancelling...' : 'Cancel my plan anyway'}
                </button>
              </div>
            </div>
          )}

          {/* Skip / no thanks */}
          <div className="border-t border-border pt-6">
            <button
              onClick={handleSkip}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
            >
              Skip — take me back to billing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
